import { useState } from 'react';
import { pagesPubliquesApi } from '../../api/resources';
import { dateSuisse, montantSuisse, quantiteLisible } from '../../lib/lien-public';
import { C } from '../../theme/palette';

/**
 * La page de commande d'un client, ouverte par son lien.
 *
 * <p>Le client y voit le stock du magasin à ses prix — le prix public barré,
 * sa remise, son prix net — choisit des quantités et envoie sa commande. La
 * maquette simulait cette page dans l'écran du commerçant (« Aperçu client »)
 * et faisait transiter la commande par un code à recopier ; ici la commande
 * arrive directement au bureau, « à préparer ». Le client est vouvoyé, comme
 * sur toute page qui lui est remise.
 *
 * @param {object} props propriétés du composant
 * @param {string} props.jeton jeton porté par le lien
 * @param {object} props.page la page de commande, telle que le serveur la sert
 * @returns {import('react').ReactElement} le catalogue à commander
 */
export function CommandeClient({ jeton, page }) {
  const { commande, societe, client } = page;
  const devise = commande?.devise || 'CHF';
  const articles = commande?.articles || [];
  const [quantites, setQuantites] = useState({});
  const [recherche, setRecherche] = useState('');
  const [adresse, setAdresse] = useState('');
  const [envoi, setEnvoi] = useState({ enCours: false, erreur: '' });
  const [recu, setRecu] = useState(null);

  const lignes = articles
    .map((a) => ({ ...a, quantite: Number(quantites[a.reference]) || 0 }))
    .filter((l) => l.quantite > 0);
  const total = lignes.reduce((s, l) => s + l.quantite * Number(l.prixNet || 0), 0);
  const filtre = recherche.trim().toLowerCase();
  const visibles = articles.filter(
    (a) =>
      !filtre ||
      (a.nom || '').toLowerCase().includes(filtre) ||
      (a.categorie || '').toLowerCase().includes(filtre) ||
      (a.reference || '').toLowerCase().includes(filtre),
  );
  // Les rayons, dans l'ordre où le magasin les a saisis.
  const rayons = Array.from(new Set(visibles.map((a) => a.categorie || 'Divers')));

  const envoyer = async () => {
    setEnvoi({ enCours: true, erreur: '' });
    try {
      const reponse = await pagesPubliquesApi.commande(jeton, {
        adresseLivraison: adresse.trim() || null,
        lignes: lignes.map((l) => ({ reference: l.reference, quantite: l.quantite })),
      });
      setRecu(reponse);
      setEnvoi({ enCours: false, erreur: '' });
      try {
        window.scrollTo(0, 0);
      } catch {
        /* sans défilement : la confirmation se lit quand même */
      }
    } catch (e) {
      setEnvoi({
        enCours: false,
        erreur: e?.message || 'La commande n’a pas pu être envoyée. Réessayez dans un instant.',
      });
    }
  };

  if (recu) {
    return (
      <section
        role="status"
        style={{ ...carte, background: C.greenL, border: '1px solid ' + C.green }}
      >
        <div style={{ fontSize: 34, textAlign: 'center' }}>✓</div>
        <h2 style={{ fontSize: 18, margin: '6px 0', color: C.green, textAlign: 'center' }}>
          Merci, votre commande est envoyée
        </h2>
        <p style={{ fontSize: 14, color: C.grey, margin: 0, lineHeight: 1.6, textAlign: 'center' }}>
          Commande <b style={{ color: C.ink }}>{recu.numero}</b> du {dateSuisse(recu.date)} —{' '}
          <b style={{ color: C.ink }}>
            {recu.devise || devise} {montantSuisse(recu.total)}
          </b>{' '}
          hors TVA. {societe?.nom} la prépare et vous livre.
        </p>
        <button
          type="button"
          style={{
            ...bouton,
            marginTop: 16,
            background: C.white,
            color: C.navy,
            border: '1px solid ' + C.border,
          }}
          onClick={() => {
            setRecu(null);
            setQuantites({});
          }}
        >
          Passer une autre commande
        </button>
      </section>
    );
  }

  return (
    <>
      <section style={carte}>
        <h1 style={{ fontSize: 22, margin: 0, fontWeight: 900 }}>Votre commande</h1>
        <div style={{ fontSize: 13, color: C.grey, marginTop: 4 }}>
          {client} · prix nets hors TVA, vos remises déduites
        </div>
        <input
          aria-label="Rechercher un article"
          placeholder="Rechercher un article ou un rayon…"
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
          style={{ ...champ, marginTop: 12 }}
        />
      </section>

      {rayons.map((rayon) => (
        <section key={rayon} style={carte}>
          <div style={etiquette}>{rayon}</div>
          {visibles
            .filter((a) => (a.categorie || 'Divers') === rayon)
            .map((a) => {
              const remise = Number(a.remise || 0);
              return (
                <div
                  key={a.reference}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 0',
                    borderTop: '1px solid ' + C.border,
                    fontSize: 14,
                  }}
                >
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{a.nom}</div>
                    <div style={{ fontSize: 12, color: C.grey, marginTop: 2 }}>
                      {remise > 0 && (
                        <span style={{ textDecoration: 'line-through', marginRight: 6 }}>
                          {montantSuisse(a.prixBase)}
                        </span>
                      )}
                      <b style={{ color: C.ink }}>
                        {devise} {montantSuisse(a.prixNet)}
                      </b>
                      {a.unite ? ' / ' + a.unite : ''}
                      {remise > 0 ? ` · remise ${quantiteLisible(remise)} %` : ''}
                      {a.tauxTva != null ? ` · TVA ${quantiteLisible(a.tauxTva)} %` : ''}
                      {!a.disponible ? ' · sur commande' : ''}
                    </div>
                  </div>
                  <input
                    aria-label={'Quantité ' + a.nom}
                    inputMode="decimal"
                    placeholder="0"
                    value={quantites[a.reference] ?? ''}
                    onChange={(e) =>
                      setQuantites((p) => ({
                        ...p,
                        [a.reference]: e.target.value.replace(',', '.'),
                      }))
                    }
                    style={{ ...champ, width: 64, textAlign: 'center', fontWeight: 800 }}
                  />
                </div>
              );
            })}
        </section>
      ))}
      {articles.length === 0 && (
        <section style={{ ...carte, textAlign: 'center', color: C.muted }}>
          Aucun article n’est proposé pour le moment.
        </section>
      )}

      <section style={carte}>
        <div style={etiquette}>Récapitulatif</div>
        {lignes.length === 0 ? (
          <div style={{ fontSize: 13, color: C.muted }}>
            Indiquez une quantité en face des articles souhaités.
          </div>
        ) : (
          lignes.map((l) => (
            <div
              key={l.reference}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
                fontSize: 13,
                padding: '4px 0',
              }}
            >
              <span>
                {quantiteLisible(l.quantite)} {l.unite || ''} × {l.nom}
              </span>
              <b>{montantSuisse(l.quantite * Number(l.prixNet || 0))}</b>
            </div>
          ))
        )}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 18,
            fontWeight: 900,
            borderTop: '2px solid ' + C.ink,
            marginTop: 8,
            paddingTop: 8,
          }}
        >
          <span>Total hors TVA</span>
          <span>
            {devise} {montantSuisse(total)}
          </span>
        </div>
        <input
          aria-label="Adresse de livraison"
          placeholder="Adresse de livraison (si différente de la vôtre)"
          value={adresse}
          onChange={(e) => setAdresse(e.target.value)}
          style={{ ...champ, marginTop: 12 }}
        />
        {envoi.erreur && (
          <div role="alert" style={{ color: C.red, fontSize: 13, marginTop: 10 }}>
            {envoi.erreur}
          </div>
        )}
        <button
          type="button"
          style={{ ...bouton, marginTop: 14, opacity: lignes.length && !envoi.enCours ? 1 : 0.5 }}
          disabled={!lignes.length || envoi.enCours}
          onClick={envoyer}
        >
          ✓ Envoyer ma commande
        </button>
      </section>
    </>
  );
}

const carte = {
  background: '#fff',
  border: '1px solid ' + C.border,
  borderRadius: 14,
  padding: 18,
  marginBottom: 14,
};

const etiquette = {
  fontSize: 11,
  fontWeight: 700,
  color: C.muted,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  marginBottom: 6,
};

const champ = {
  width: '100%',
  boxSizing: 'border-box',
  border: '1px solid ' + C.border,
  borderRadius: 10,
  padding: '10px 12px',
  fontSize: 14,
  color: C.ink,
  background: '#fff',
};

const bouton = {
  display: 'block',
  width: '100%',
  textAlign: 'center',
  background: C.navy,
  color: '#fff',
  fontWeight: 800,
  fontSize: 15,
  borderRadius: 12,
  padding: '14px 16px',
  border: 'none',
  cursor: 'pointer',
};
